<template>
  <DialogFull :data="dialogSettings" @close="close">
    <template v-slot:dialog.title>
      Edit Pay Rates<v-icon
        left
        class="ml-2"
        color="info"
        @click="infoDialog = true"
        >mdi-information-outline</v-icon
      >
      <!-- Info Dialog -->
      <v-dialog v-model="infoDialog" max-width="500">
        <v-card>
          <v-card-text class="pb-0">
            Selecting an agency without selecting a worker will apply the
            specified rate to all workers on the booking from that particular
            agency.<br /><br />
            Any agency or workers not given a specific rate will be assigned to
            the default booking rate.<br /><br />
            Rates will apply to the entirety of the booking time period and will
            affect existing timesheets.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="infoDialog = false"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <template v-slot:dialog.body>
      <div class="edit-basic-details-content">
        <div
          class="text-center mb-2"
          v-if="isEditingBookingPayRates || isFetchingData"
        >
          <v-progress-circular size="30" indeterminate color="primary" />
        </div>
        <div class="col-md-6 col-sm-12" v-else>
          <v-form ref="form" v-model="formIsValid" lazy-validation>
            <v-row class="mx-0">
              <div
                v-for="(activity, index) in activities"
                :key="index"
                class="flex flex-row align-center flex-gap-20 mb-2"
              >
                <div class="half-width pl-2">
                  {{ activity.activityTypeName }}
                </div>
                <v-text-field
                  class="mt-0 half-width pr-2"
                  v-model="activity.payRateAmount"
                  v-money="moneyConfig"
                  prefix="£"
                />
              </div>
            </v-row>
            <v-row class="mx-0">
              <div
                v-for="(row, index) in rows"
                :key="row.id"
                class="d-flex w-100 row-container relative"
              >
                <v-btn
                  :class="{ 'btn-action': true, 'btn-offset': index === 0 }"
                  @click="onActionClick(index)"
                >
                  <v-icon>{{ index === 0 ? "mdi-plus" : "mdi-minus" }}</v-icon>
                </v-btn>
                <v-col cols="3" class="input-item mb-3">
                  <Select
                    id="agency"
                    :label="index === 0 ? 'Agency' : ''"
                    autocomplete
                    v-model="row.agencyId"
                    :items="activeAgencies"
                    :loading="isLoadingAgencies"
                    :rules="isRequired('Agency')"
                    @change="handAgencyChange($event, index)"
                    item-text="name"
                    item-value="id"
                    single-line
                    clearable
                  />
                </v-col>
                <v-col cols="3" class="input-item mb-3">
                  <Select
                    id="candidate"
                    :label="index === 0 ? 'Candidate' : ''"
                    autocomplete
                    v-model="row.applicationId"
                    :items="approvedCandidates"
                    :loading="isLoadingCandidates"
                    @change="handleCandidateChange($event, index)"
                    :item-text="
                      item => `${item.user.firstName} ${item.user.lastName}`
                    "
                    item-value="id"
                    single-line
                    clearable
                  />
                </v-col>
                <v-col cols="3" class="input-item mb-3">
                  <div>
                    <label v-if="index === 0" for="pre-awr">Pre AWR</label>
                    <v-text-field
                      :rules="[
                        ...isRequired('Pre AWR'),
                        ...cantBeNegativeNumber()
                      ]"
                      id="pre-awr"
                      prepend-inner-icon="mdi-currency-gbp"
                      type="number"
                      @blur="restrictToTwoDecimalPlaces(index, 'preAwr')"
                      v-model="row.preAwr"
                      step="0.05"
                      dense
                    />
                  </div>
                </v-col>
                <v-col cols="3" class="input-item mb-3">
                  <div>
                    <label v-if="index === 0" for="post-awr">Post AWR</label>
                    <v-text-field
                      id="post-awr"
                      :rules="[
                        ...isRequired('Post AWR'),
                        ...cantBeNegativeNumber()
                      ]"
                      prepend-inner-icon="mdi-currency-gbp"
                      type="number"
                      v-model="row.postAwr"
                      @blur="restrictToTwoDecimalPlaces(index, 'postAwr')"
                      step="0.05"
                      dense
                    />
                  </div>
                </v-col>
                <v-btn
                  v-if="rows?.length === 1"
                  :class="{
                    'btn-remove': true,
                    'btn-action': true,
                    'btn-offset': index === 0
                  }"
                  @click="onResetRows"
                >
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </div>
            </v-row>
          </v-form>
        </div>
      </div>
    </template>
    <template v-slot:dialog.action>
      <v-spacer />
      <PrimaryButton
        :loading="isEditingBookingPayRates"
        class="edit-basic-details-action"
        @click.native="save"
      >
        Save Changes
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import Select from "@/components/common/Select";
import DialogFull from "@/components/common/DialogFull";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import { createNamespacedHelpers } from "vuex";
import {
  BOOKINGS_NAMESPACE,
  FETCH_BOOKING_PAYRATES,
  FETCH_CURRENT_BOOKING_CANDIDATES_V2
} from "@/store/modules/bookings/actions";
import { AGENCY_NAMESPACE } from "@/store/modules/agencies";
import {
  IS_EDITING_BOOKING_PAYRATES,
  IS_LOADING_BOOKING_CANDIDATES,
  GET_CURRENT_BOOKING_AGENCY_ID,
  GET_BOOKING_PAYRATES
} from "@/store/modules/bookings/getters";
import { FETCH_ALL_AGENCIES } from "@/store/modules/agencies/action-types";
import { GET_ALL_AGENCIES } from "@/store/modules/agencies/getters";
import { CUSTOM_AGENCY_FEE_NAMESPACE } from "@/store/modules/custom-agency-pay-rates";
import { FETCH_AGGREGATED_CUSTOM_AGENCY_FEES } from "@/store/modules/custom-agency-pay-rates/actions";
import { cloneDeep, filter, map } from "lodash";
import { getAmountInApiFormat, getMonetaryAsNumber } from "@/utils/helpers";

const {
  mapState: mapBookingState,
  mapGetters: mapBookingGetters,
  mapActions: mapBookingActions
} = createNamespacedHelpers(BOOKINGS_NAMESPACE);
const {
  mapState: mapAgenciesState,
  mapActions: mapAgenciesActions,
  mapGetters: mapAgenciesGetters
} = createNamespacedHelpers(AGENCY_NAMESPACE);
const {
  mapActions: mapCustomAgencyFeeActions,
  mapState: mapCustomAgencyFeeState
} = createNamespacedHelpers(CUSTOM_AGENCY_FEE_NAMESPACE);

export default {
  name: "EditPayRates",
  components: {
    DialogFull,
    PrimaryButton,
    Select
  },
  data() {
    return {
      infoDialog: false,
      formIsValid: false,
      moneyConfig: { precision: 2 },
      rows: [
        {
          agencyId: null,
          applicationId: null,
          preAwr: null,
          postAwr: null
        }
      ],
      activities: [],
      initialActivities: [],
      isFetchingData: false
    };
  },
  computed: {
    ...mapCustomAgencyFeeState({
      aggregatedCustomAgencyFees: state => state.aggregatedCustomAgencyFees
    }),
    ...mapBookingState({
      currentBookingCandidates: state => state.currentBookingCandidates,
      currentBookingId: state => state.currentBooking.id,
      currentBookingTitle: state => state.currentBooking.title,
      currentClientId: state => state.currentBooking.client.id
    }),
    ...mapBookingGetters({
      isEditingBookingPayRates: IS_EDITING_BOOKING_PAYRATES,
      isLoadingCandidates: IS_LOADING_BOOKING_CANDIDATES,
      getCurrentBookingAgencyId: GET_CURRENT_BOOKING_AGENCY_ID,
      bookingPayRates: GET_BOOKING_PAYRATES
    }),
    ...mapAgenciesState({
      isLoadingAgencies: state => state.isFetchingData
    }),
    ...mapAgenciesGetters({
      activeAgencies: GET_ALL_AGENCIES
    }),
    dialogSettings() {
      return {
        title: "Edit Pay Rates",
        dialog: this.isOpen
      };
    },
    approvedCandidates() {
      return filter(
        this.currentBookingCandidates,
        candidate => candidate.status === "approved"
      );
    }
  },
  methods: {
    ...mapCustomAgencyFeeActions({
      fetchAggregatedCustomAgencyFees: FETCH_AGGREGATED_CUSTOM_AGENCY_FEES
    }),
    ...mapAgenciesActions({
      fetchAllAgencies: FETCH_ALL_AGENCIES
    }),
    ...mapBookingActions({
      fetchCurrentBookingCandidates: FETCH_CURRENT_BOOKING_CANDIDATES_V2,
      fetchBookingPayRates: FETCH_BOOKING_PAYRATES
    }),
    // Restrict the input to two decimal places
    // in the future, we should fetch currency config from the server
    restrictToTwoDecimalPlaces(index, field) {
      let value = this.rows[index][field];
      if (value && value.includes(".")) {
        let roundedValue = Number(value).toFixed(2);
        this.$set(this.rows[index], field, roundedValue);
      }
    },
    handAgencyChange(value, index) {
      this.rows = this.rows.map((row, i) =>
        i === index ? { ...row, agencyId: value } : row
      );
    },
    handleCandidateChange(value, index) {
      const agencyId = this.getCurrentBookingAgencyId(value);
      this.rows = this.rows.map((row, i) =>
        i === index ? { ...row, applicationId: value, agencyId } : row
      );
    },
    addRow() {
      this.rows = [
        ...this.rows,
        {
          agencyId: null,
          applicationId: null,
          preAwr: null,
          postAwr: null
        }
      ];
    },
    removeRow(index) {
      this.rows = this.rows.filter((_, rowIndex) => rowIndex !== index);
    },
    onResetRows() {
      this.rows = [
        {
          agencyId: null,
          applicationId: null,
          preAwr: null,
          postAwr: null
        }
      ];
    },
    onActionClick(index) {
      if (index === 0) {
        this.addRow();
      } else {
        this.removeRow(index);
      }
    },
    isRequired(field) {
      return [value => !!value || `${field} is required`];
    },
    cantBeNegativeNumber() {
      return [value => value >= 0 || "The number can't be less than 0"];
    },
    async save() {
      this.showValidationErrors = true;
      const isValid = await this.$refs.form.validate();
      const activitiesHasChanged = this.activities.some((newRate, index) => {
        const oldRate = this.initialActivities[index] || {};
        const newAmount = getAmountInApiFormat(
          getMonetaryAsNumber(newRate.payRateAmount)
        );
        const oldAmount = String(oldRate.payRateAmount ?? "");
        return newAmount !== oldAmount;
      });
      const payRates = activitiesHasChanged
        ? map(this.activities, activity => ({
            activityTypeName: activity.activityTypeName,
            payRateAmount: getAmountInApiFormat(
              getMonetaryAsNumber(activity.payRateAmount)
            )
          }))
        : [];
      if (isValid) {
        this.$emit("edit", {
          bookingId: this.currentBookingId,
          customAgencyRates: this.rows,
          payRates
        });
      }
    },
    close() {
      this.$emit("close");
    }
  },
  watch: {
    aggregatedCustomAgencyFees(newVal) {
      this.rows = newVal.map(data => ({
        ...data
      }));
    },
    bookingPayRates(newValue) {
      const payrates = filter(
        newValue,
        payRate =>
          Date.parse(payRate.validToDate) > Date.now() && payRate.isDefault
      );
      this.activities = map([...payrates], payrate => ({
        activityTypeName: payrate.activityTypeName,
        payRateAmount: payrate.payRateAmount
      }));
      this.initialActivities = cloneDeep(this.activities);
    }
  },
  async created() {
    this.isFetchingData = true;
    if (!this.activeAgencies?.length) {
      await this.fetchAllAgencies();
    }
    if (!this.currentBookingCandidates?.length) {
      await this.fetchCurrentBookingCandidates();
    }
    await this.fetchAggregatedCustomAgencyFees({
      bookingId: this.currentBookingId
    });
    if (!this.bookingPayRates || !this.bookingPayRates.length) {
      await this.fetchBookingPayRates({
        bookingId: this.currentBookingId,
        params: { filter }
      });
    }
    this.isFetchingData = false;
  },
  props: {
    isOpen: Boolean
  }
};
</script>

<style lang="scss">
.edit-basic-details-content {
  display: flex;
  justify-content: center;
  margin-top: 2rem;

  .relative {
    position: relative;
  }

  .v-icon {
    margin-top: 10px;
  }

  .btn-action {
    position: absolute;
    top: 20px;
    left: -66px;
    height: 48px !important;
  }

  .btn-offset {
    top: 45px !important;
  }

  .btn-remove {
    left: inherit;
    right: -66px;
  }

  .row-container {
    position: relative;
  }

  .v-row {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 0 !important;
  }

  .input-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 0 !important;
    padding: 10px;
  }

  .v-text-field {
    margin-top: 10px;
  }
}
</style>
